// A webinar's or a course recording's own thumbnail in admin, the 1 block both editors draw (Adam, 9
// Oct, for webinars; decision 47 as he changed it on 12 Oct, for every course recording, "exactly as a
// webinar's"): moved here from WebinarsView.jsx unchanged, so the 2 can't drift apart.
//   usePreviews(api, rows, target)   the saved thumbnails as staff see them, through sign-file's
//                                    webinars or recordings target (data.js signThumbnailPreviews)
//   <ThumbnailBlock ... />           the 16:9 preview, Add a thumbnail, Change it, Take it off, the hint,
//                                    the 16:9 warning and what's happened
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '../ui/index.js'
import { dateBlock } from '../../lib/format.js'
import { COPY } from './copy.js'
import { isSixteenNine } from './model.js'
import { signThumbnailPreviews } from './data.js'

// A thumbnail uploaded on this visit, by its path: its preview, so it shows at once after the save
// rather than waiting on a signed link, until sign-file answers for it (webinars' and recordings' paths
// are each their own upload, so 1 map serves both)
export const uploaded = new Map()

// A webinar's or a recording's thumbnail as it stands: its id and the path on it
const slot = w => `${w.id}:${w.thumbnail_path}`
// 2 links the same once the browser has written them out in full (an <img>'s src is)
function sameLink(a, b) {
  try { return new URL(a, window.location.href).href === new URL(b, window.location.href).href } catch { return a === b }
}

// The webinars' (or, with target 'recordings', the recordings') thumbnails as staff see them, signed
// only for those that have 1, again when they change.
// A preview that won't load (its 2-minute link ran out, the editor drawn again after that, or the file
// gone) is signed once more, as members' cards are (free/thumbnails.js); if that fails too, or sign-file
// answers and signs none for it, the editor says it won't load, since members then see the date, in
// place of an empty grey frame. sign-file not answering (no answer, or a 5xx) is tried once more
// (data.js); if it still doesn't answer, or refuses, what's there stays: a link is never swapped for
// "won't load". Once sign-file has answered for a saved thumbnail, its answer wins over 1 uploaded on
// this visit: the daily clear-up may have removed the file before the save, and members then see the date.
// A link that loads earns its slot another once-more, so a link that runs out again later in the visit is
// signed again too; a file that never loads still can't loop.
//   preview.url(w)           the picture uploaded on this visit or the signed link, or null
//   preview.missing(w)       true once it wouldn't sign or load
//   preview.failed(w, src)   an <img>'s onError, with the link that failed
//   preview.loaded(w, src)   an <img>'s onLoad, with the link that loaded
export function usePreviews(api, webinars, target = 'webinars') {
  // slot -> link, or false where it wouldn't sign or load; a slot not here yet is being signed
  const [urls, setUrls] = useState({})
  const current = useRef(urls)
  current.current = urls
  const retried = useRef(new Set())
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => { live.current = false }
  }, [])
  // { slot: link or false } for the webinars in a list that sign-file answered for
  const sign = useCallback(list => signThumbnailPreviews(api, list, target).catch(() => ({}))
    .then(got => Object.fromEntries(list.filter(w => w.id in got).map(w => [slot(w), got[w.id]]))), [api, target])
  // what sign-file answered, kept: false never replaces a link already there
  const take = useCallback(got => setUrls(u => {
    const next = { ...u }
    for (const [s, v] of Object.entries(got)) if (v || typeof next[s] !== 'string') next[s] = v
    return next
  }), [])
  const withThumb = webinars.filter(w => w.thumbnail_path)
  const key = withThumb.map(slot).join(',')
  useEffect(() => {
    if (!key) return
    let on = true
    sign(withThumb).then(got => { if (on) take(got) })
    return () => { on = false }
  }, [key])

  const failed = useCallback((w, src) => {
    if (!w?.thumbnail_path) return
    const s = slot(w)
    const now = current.current[s]
    // a link already let go (the list's picture and the editor's failing together count once)
    if (typeof now !== 'string' || (src && !sameLink(now, src))) return
    if (retried.current.has(s)) {
      current.current = { ...current.current, [s]: false }
      setUrls(u => ({ ...u, [s]: false }))
      return
    }
    // once more, with a new link
    retried.current.add(s)
    const { [s]: _, ...rest } = current.current
    current.current = rest
    setUrls(u => { const next = { ...u }; delete next[s]; return next })
    sign([w]).then(got => { if (live.current) take(got) })
  }, [sign, take])

  // the slot's own signed link loaded (not a picture uploaded on this visit): it may be signed once more again
  const loaded = useCallback((w, src) => {
    if (!w?.thumbnail_path) return
    const s = slot(w)
    if (src && sameLink(current.current[s], src)) retried.current.delete(s)
  }, [])

  return {
    url: w => {
      const v = w?.thumbnail_path ? urls[slot(w)] : false
      return v === false ? null : uploaded.get(w.thumbnail_path) || v || null
    },
    missing: w => Boolean(w?.thumbnail_path) && urls[slot(w)] === false,
    failed,
    loaded,
  }
}

// The Thumbnail block: a 16:9 preview as members see it (the picture filling the frame, or the date on
// its grey panel where there's none, or where the 1 on it won't load, signed once more first: missing,
// with a line under the frame saying so, which is the frame's name for screen readers too), then for
// owner and admin Add a thumbnail, or Change it and Take it off, the hint, the 16:9 warning and what's
// happened. Support sees the preview only, and the line without what to do about it. Focus stays on the
// upload button while it uploads (aria-disabled, never disabled, which would drop it), and Take it off,
// whose own button then goes, moves it to Add a thumbnail.
// panel: what members see in place of the date where there's no picture, for a product whose cards
// draw their own panel (Property Cashflow For Life's dark green module panel, Adam, 12 Oct):
// { className, node, none, broken, brokenRead }, the frame's class and contents and its 3 lines
export function ThumbnailBlock({ ids, edits, path, url, missing = false, when, panel = null, busy, note, cropped, onCropped, onPick, onTakeOff, onError, onLoad }) {
  const input = useRef(null)
  const upload = useRef(null)
  const block = when ? dateBlock(when) : null
  const none = !path || missing
  const broken = Boolean(path) && missing
  const noneWords = panel?.none || COPY.thumbnailNone
  const brokenWords = edits ? panel?.broken || COPY.thumbnailBroken : panel?.brokenRead || COPY.thumbnailBrokenRead
  return (
    <div className="adc-thumb" role="group" aria-labelledby={`${ids}-t`}>
      <span className="adc-field-label" id={`${ids}-t`}>{COPY.thumbnail}</span>
      <div className="adc-thumb-row">
        <div className="adc-thumb-pic">
          <span className={`adc-thumb-frame${none && panel?.className ? ` ${panel.className}` : ''}`} role="img"
            aria-label={broken ? brokenWords : none ? noneWords : COPY.thumbnailPreview}>
            {url && !none && (
              <img key={url} src={url} alt="" decoding="async" onError={e => onError?.(e.currentTarget.src)}
                onLoad={e => { onCropped(!isSixteenNine(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)); onLoad?.(e.currentTarget.src) }} />
            )}
            {none && panel?.node}
            {none && !panel && block && (
              <span className="adc-thumb-date" aria-hidden="true">
                <span className="adc-thumb-edge">{block.dow}</span>
                <span className="adc-thumb-day">{block.day}</span>
                <span className="adc-thumb-edge">{block.mon}</span>
              </span>
            )}
          </span>
          {/* the frame's name already says it, so screen readers aren't told twice */}
          {broken && <p className="adc-thumb-broken" aria-hidden="true">{brokenWords}</p>}
        </div>
        {edits && (
          <div className="adc-thumb-side">
            <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="adc-file" tabIndex={-1} aria-hidden="true"
              onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; onPick(file) }} />
            <div className="adc-thumb-buttons">
              <Button ref={upload} size="sm" aria-disabled={busy || undefined} aria-busy={busy || undefined}
                onClick={() => { if (!busy) input.current?.click() }}>
                {path ? COPY.thumbnailChange : COPY.thumbnailAdd}
              </Button>
              {path && <Button size="sm" disabled={busy} onClick={() => { onTakeOff(); upload.current?.focus() }}>{COPY.thumbnailOff}</Button>}
            </div>
            <span className="adc-hint">{COPY.thumbnailHint}</span>
            {!none && cropped && <p className="adc-thumb-warn">{COPY.thumbnailCropped}</p>}
            {note && <p className={note.problem ? 'ad-error adc-thumb-note' : 'adc-thumb-note'} role={note.problem ? 'alert' : 'status'}>{note.text}</p>}
          </div>
        )}
      </div>
    </div>
  )
}
